import React, { useState, useMemo, useCallback } from 'react';
import SearchBar from '../common/SearchBar';
import './PageStyles.css';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
  status: 'active' | 'error' | 'warning';
  lastSync: string;
}

const Integrations: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'linkedin',
      name: 'LinkedIn',
      description: 'Professional networking and B2B marketing',
      icon: '/assets/linkedin-icon.svg',
      connected: true,
      status: 'active',
      lastSync: '2 minutes ago'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      description: 'Visual content and influencer marketing',
      icon: '/assets/instagram-icon.svg',
      connected: true,
      status: 'active',
      lastSync: '5 minutes ago'
    },
    {
      id: 'facebook',
      name: 'Facebook',
      description: 'Social media marketing and advertising',
      icon: '/assets/facebook-icon.svg',
      connected: true,
      status: 'warning',
      lastSync: '2 hours ago'
    },
    {
      id: 'twitter',
      name: 'X (Twitter)',
      description: 'Real-time engagement and news sharing',
      icon: '/assets/twitter-icon.svg',
      connected: false,
      status: 'error',
      lastSync: 'Never'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      description: 'Short-form video content marketing',
      icon: '/assets/tiktok-icon.svg',
      connected: true,
      status: 'active',
      lastSync: '10 minutes ago'
    },
    {
      id: 'telegram',
      name: 'Telegram',
      description: 'Messaging and community building',
      icon: '/assets/telegram-icon.svg',
      connected: false,
      status: 'error',
      lastSync: 'Never'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      description: 'Direct customer communication',
      icon: '/assets/whatsapp-icon.svg',
      connected: true,
      status: 'active',
      lastSync: '1 minute ago'
    }
  ]);

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? {
              ...integration,
              connected: !integration.connected,
              status: integration.connected ? 'error' : 'active',
              lastSync: integration.connected ? 'Never' : 'Just now'
            }
          : integration
      )
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const connectedCount = integrations.filter(i => i.connected).length;

  // Filter integrations based on search query
  const filteredIntegrations = useMemo(() => {
    if (!searchQuery.trim()) return integrations;
    
    return integrations.filter(integration =>
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [integrations, searchQuery]);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    return integrations.map(integration => integration.name);
  }, [integrations]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">Integrations</h1>
            <p className="page-subtitle">Connect your social media platforms and manage your network</p>
          </div>
          <div className="header-actions">
            <SearchBar
              placeholder="Search integrations..."
              onSearch={handleSearch}
              onClear={handleClearSearch}
              suggestions={searchSuggestions}
              showSuggestions={true}
              className="integrations-search"
            />
          </div>
        </div>
        <div className="header-stats">
          <div className="stat-badge">
            <span className="stat-number">{connectedCount}</span>
            <span className="stat-label">Connected</span>
          </div>
          <div className="stat-badge">
            <span className="stat-number">{integrations.length - connectedCount}</span>
            <span className="stat-label">Available</span>
          </div>
          {searchQuery && (
            <div className="stat-badge">
              <span className="stat-number">{filteredIntegrations.length}</span>
              <span className="stat-label">Found</span>
            </div>
          )}
        </div>
      </div>

      {/* Integration Cards */}
      <div className="integrations-grid">
        {filteredIntegrations.length > 0 ? (
          filteredIntegrations.map((integration) => (
          <div key={integration.id} className={`integration-card-new ${integration.connected ? 'connected' : 'disconnected'}`}>
            <div className="integration-header">
              <div className="integration-icon-wrapper">
                <img 
                  src={integration.icon} 
                  alt={integration.name}
                  className="integration-icon-img"
                />
                <div 
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(integration.status) }}
                ></div>
              </div>
              <div className="integration-info">
                <h3 className="integration-name">{integration.name}</h3>
                <p className="integration-description">{integration.description}</p>
              </div>
            </div>

            <div className="integration-details">
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status-${integration.status}`}>
                  {integration.connected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last sync:</span>
                <span className="detail-value">{integration.lastSync}</span>
              </div>
            </div>

            <div className="integration-actions">
              <button 
                className={`toggle-btn ${integration.connected ? 'disconnect' : 'connect'}`}
                onClick={() => toggleIntegration(integration.id)}
              >
                {integration.connected ? 'Disconnect' : 'Connect'}
              </button>
              {integration.connected && (
                <button className="settings-btn-integration">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2583 9.77251 19.9887C9.5799 19.7191 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.74168 9.96512 4.01127 9.77251C4.28087 9.5799 4.48571 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))
        ) : (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No integrations found</h3>
            <p>Try adjusting your search criteria or browse all available integrations.</p>
            <button 
              className="clear-search-btn"
              onClick={handleClearSearch}
            >
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="help-section">
        <div className="help-card">
          <h3>Need Help?</h3>
          <p>Learn how to connect and configure your social media integrations</p>
          <div className="help-actions">
            <button className="help-btn primary">View Documentation</button>
            <button className="help-btn secondary">Contact Support</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integrations; 